import React, { useState } from "react";

import moment from "moment/moment";

import { useGetCropMixComposerSettings, useLocation, useNavigate, useTranslation } from "utils";

import { Button, Flex, Icon, Popover, Spin } from "components";

import { CMC_ROUTES } from "pages/app/crop-mix-composer/CropMixComposerOverlay";

export const CropMixComposerHeader = ({
  basePath,
  isDrawer,
  activeCropMixId,
  isCommercialCropMix,
  isPageLoading,
}: {
  basePath: string;
  isDrawer?: boolean;
  activeCropMixId?: string;
  isCommercialCropMix?: boolean;
  isPageLoading?: boolean;
}) => {
  const { t } = useTranslation("cropMixComposer");
  const location = useLocation();
  const navigate = useNavigate();
  const { data: settingsCMC, isPending: isPendingSettingsCMC } = useGetCropMixComposerSettings();

  const cropMixes = settingsCMC || [];

  const [isOpen, setIsOpen] = useState<boolean>(false);

  const handleMixSelection = (mixId: string) => {
    if (!mixId) return;

    setIsOpen(false);
    navigate(`${basePath}cmc/${mixId}${location.search}`);
  };

  const BackToCustomMixButton = () => (
    <Button
      size="small"
      type="secondary"
      style={{ width: "fit-content" }}
      onClick={() => navigate(`${basePath}cmc/${activeCropMixId}/${CMC_ROUTES.OVERVIEW}${location.search}`)}
    >
      {t("backToCustomMix")}
    </Button>
  );

  const CropMixesList = (
    <Flex className="crop-mix-selection-menu navigation-list" vertical>
      <Flex className="crop-mix-selection-menu navigation-list container" vertical>
        {cropMixes.map((mix, i) => (
          <Flex
            key={mix.id}
            className="crop-mix-selection-menu navigation-list item"
            data-testid={`crop-mix-id-${i}`}
            align={"center"}
            justify={"flex-start"}
            gap={"0.5rem"}
            onClick={() => handleMixSelection(mix.id)}
          >
            {activeCropMixId === mix.id ? <Icon iconName="tabler/circle-check" color="black" /> : <span style={{ minWidth: "1.5rem" }} />}
            <Flex vertical>
              <p className="mix-name">{mix.name}</p>
              <p className="last-updated-date-text paragraph-xs">{moment(mix.lastUpdated).format("DD.MM.YYYY")}</p>
            </Flex>
          </Flex>
        ))}
      </Flex>
      <Flex
        className="new-crop-mix-button"
        data-testid={"crop-mix-selection-menu-create-new-button"}
        align={"center"}
        justify={"flex-start"}
        gap={"1rem"}
        onClick={() => {
          setIsOpen(false);
          navigate(`${basePath}cmc${location.search}`);
        }}
      >
        <Icon iconName="klim/plus" color="green" />
        <p>{t("createNew")}</p>
      </Flex>
    </Flex>
  );

  return (
    <Flex className="crop-mix-composer-drawer-header" align="center" justify={isCommercialCropMix ? "space-between" : "flex-start"} gap={"0.5rem"} flex={1}>
      <Flex align="center" gap={"0.5rem"}>
        {isDrawer ? (
          <>
            <Icon iconName="klim/plant-ai" size="m" />
            <h3>{t("drawerTitle")}</h3>
          </>
        ) : null}
        {isDrawer ? null : (
          <Popover
            dataTestId="crop-mix-selection-menu-popover"
            className="crop-mix-selection-menu"
            placement="bottom"
            arrow={false}
            content={CropMixesList}
            trigger="click"
            open={isOpen}
            onOpenChange={() => setIsOpen(!isOpen)}
          >
            {isPendingSettingsCMC || isPageLoading ? (
              <Spin size="default" />
            ) : cropMixes.length < 1 || !activeCropMixId ? null : (
              <Flex
                data-testid="crop-mix-selection"
                className="crop-mix-selection-menu button pointer"
                justify="center"
                align="center"
                onClick={() => setIsOpen(!isOpen)}
                gap="0.5rem"
              >
                <p>{cropMixes.find((mix) => mix.id === activeCropMixId)?.name}</p>
                <Icon className={`crop-mix-selection-menu collapse-chevron ${!isOpen && "reverse"}`} iconName="tabler/chevron-up" />
              </Flex>
            )}
          </Popover>
        )}
      </Flex>
      {isCommercialCropMix ? <BackToCustomMixButton /> : null}
    </Flex>
  );
};
